/**
 * Shared helpers + consts used by more than one Flight School activity.
 * Extracted verbatim from the former activities.tsx monolith — no behaviour
 * change.
 */
import { CSSProperties } from "react";
import { colors } from "@/ui";
import { callWithAuth, postDictionaryBulkAdd } from "@/api/Api";
import { BulkAddItem } from "@shared_types";
import { CognateWord } from "../../lib/activityTypes";

/**
 * Persist a Flight School word's status to the dictionary — iOS
 * `updateAndSyncGlobalWordStatus(word.id, status, …)`. FS cognate words are
 * authored content with no backend ids, so (like onboarding flashcards) we
 * bulk-add by text/translation. Fire-and-forget: FS progress must never block.
 */
export function syncFsWord(word: CognateWord, status: "known" | "learning") {
  const items: BulkAddItem[] = [
    { text: word.spanish, translation: word.english, status },
  ];
  void callWithAuth(postDictionaryBulkAdd, { items }).catch(() => {});
}

/**
 * Shared class on every FS option pill. Drives the iOS `Button3D` press
 * feel — a `:active` translateY(2px) that sinks the pill into its own
 * bottom-edge depth shadow (Button3D.tsx onPressIn scale + drop shadow).
 *
 * Central option-pill base style inherited by MCQ / Match / Listening /
 * VideoFill / Fill activities. Mirrors iOS `Button3D` (Button3D.tsx): a raised,
 * full-pill chip with a white fill and a soft drop shadow — NO 2px grey outline.
 *
 * Full-pill radius (9999) matches Button3D's `layout.height / 2` rounding —
 * the chip is always a perfect stadium regardless of height.
 */
export const optionBaseStyle: CSSProperties = {
  width: "100%",
  padding: "16px 18px",
  // Full-pill radius (iOS Button3D: borderRadius = height / 2).
  borderRadius: 9999,
  // No outline — the 3D look comes from the fill + bottom-edge depth shadow,
  // not a border (iOS Button3D draws no stroke on the default chip). Use the
  // longhand border props (not the `border` shorthand) so a caller's
  // `borderColor` override composes without React's shorthand/longhand
  // "removing a conflicting style property" rerender warning.
  borderWidth: 0,
  borderStyle: "solid",
  borderColor: "transparent",
  background: "#fff",
  // Soft drop shadow ALL AROUND the button (iOS Button3D look), not a
  // bottom-only edge.
  boxShadow: "0 2px 8px rgba(0,0,0,0.10), 0 1px 2px rgba(0,0,0,0.06)",
  fontSize: 18,
  fontWeight: 600,
  color: "var(--color-text-primary)",
  cursor: "pointer",
  textAlign: "center",
  transition: "transform .06s ease, box-shadow .06s ease, background .15s ease, color .15s ease",
};

/**
 * Per-state overrides spread on top of `optionBaseStyle`.
 *
 * iOS `ActivityButton.getButtonColors` (ActivityButton.tsx:154-189) policy:
 * any COLORED fill carries WHITE text — correct (green), incorrect (red), and
 * selected/matched (blue) all pair with white. The default chip keeps its
 * white fill + primary text.
 *
 * - correct   → solid green fill, white label, no border tint
 * - incorrect → solid red fill, white label, no border tint
 * - selected  → solid blue fill, white label (Match's mid-selection state)
 * - idle      → no override (stays the raised white pill)
 */
export function optionState(
  state: "idle" | "correct" | "incorrect" | "selected",
): CSSProperties {
  if (state === "correct")
    return { background: colors.activityCorrect, color: colors.white };
  if (state === "incorrect")
    return { background: colors.activityIncorrect, color: colors.white };
  if (state === "selected")
    return { background: colors.blue, color: colors.white };
  return {};
}

export interface ActivityProps<T> {
  data: T;
  onComplete: () => void;
}
